/**
 * Shared permission-check helper.
 *
 * Mirrors the logic already used in Sidebar.jsx: SUPER_ADMIN bypasses
 * everything, otherwise a permission is granted if it (or its wildcard
 * `<resource>.*`) shows up in either the user's role permissions or their
 * direct permissions.
 */
export const hasPermission = (user, permission) => {
  if (!permission) return true;
  if (!user) return false;
  if (user.globalRole === 'SUPER_ADMIN') return true;

  const matches = (list) =>
    list?.some((p) => p.name === permission || p.name === permission.split('.')[0] + '.*');

  return matches(user.rolePermissions) || matches(user.directPermissions);
};

/**
 * The page a user lands on (after login, on "/", on unknown URLs): the
 * dashboard for users allowed to see it, otherwise the orders list.
 */
export const getHomePath = (user) => {
  if (hasPermission(user, 'dashboard.read')) return '/dashboard';
  if (hasPermission(user, 'orders.read')) return '/orders';
  return '/profile';
};

/**
 * Maps a notification's `referenceType` (set by the backend, mirrors the
 * StockMovement referenceType/referenceId convention) to the frontend route
 * for that entity's detail page. Extend this as new notification-producing
 * modules are added.
 */
const REFERENCE_ROUTES = {
  ORDER: (id) => `/orders/${id}`,
  PURCHASE_ORDER: (id) => `/purchase-orders/${id}`,
};

/** Returns the detail-page path for a notification, or null if unknown/no reference. */
export const getNotificationTargetPath = (notification) => {
  if (!notification?.referenceType || !notification?.referenceId) return null;
  const buildPath = REFERENCE_ROUTES[notification.referenceType];
  return buildPath ? buildPath(notification.referenceId) : null;
};
